import { Divider, Popconfirm } from 'antd';
import i18n from 'i18next';
import styled from 'styled-components';

import CopyClipboard from 'components/CopyClipboard';
import {
  AttachmentName,
  AttachmentNameRow,
} from 'components/RecordAttachments/AttachmentGridItem/AttachmentGridItem.style';
import { type AtlasGqlAttachment } from 'types/atlas-graphql';
import { formatBytes, formatUnixTimestamp } from 'utils/format';

import { None, sortBy, truncateTooltipper } from './helpers';

const ActionButton = styled.button`
  background: none;
  color: ${({ theme }) => theme.teal};
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
`;

export const name = {
  title: i18n.t('attachments.fileName', 'File Name'),
  key: 'name',
  sorter: sortBy('name'),
  render: (record: AtlasGqlAttachment) => {
    const { url, name } = record || {};

    return [url, name].every(Boolean) ? (
      <AttachmentNameRow>
        <CopyClipboard value={name!} compact>
          <AttachmentName
            as="a"
            href={url!}
            rel="noopener noreferrer"
            target="_blank"
            data-testid="attachment-name"
          >
            {name}
          </AttachmentName>
        </CopyClipboard>
      </AttachmentNameRow>
    ) : (
      <None />
    );
  },
};

export const category = {
  title: i18n.t('attachments.category', 'Category'),
  key: 'category',
  sorter: sortBy('category'),
  render: (record: AtlasGqlAttachment) => {
    const { category } = record || {};
    const name = category?.name;
    return name ? truncateTooltipper(name, 40) : <None />;
  },
};

export const description = {
  title: i18n.t('table.description', 'Description'),
  key: 'description',
  sorter: sortBy('description'),
  render: (record: AtlasGqlAttachment) => {
    const { description } = record || {};
    return description ? (
      truncateTooltipper(description, 35)
    ) : (
      <None message={i18n.t('attachments.noDescription', 'no description')} />
    );
  },
};

export const size = {
  title: i18n.t('table.size', 'Size'),
  key: 'size',
  sorter: sortBy('size'),
  render: (record: AtlasGqlAttachment) => {
    const { size } = record || {};
    return size != null && Number.isFinite(size) ? (
      formatBytes(size)
    ) : (
      <None message={i18n.t('attachments.noSize', 'no size')} />
    );
  },
};

export const created = {
  title: i18n.t('attachments.creationTime', 'Creation Time'),
  key: 'created',
  sorter: (a: AtlasGqlAttachment, b: AtlasGqlAttachment) => {
    const getTimestamp = (attachment: AtlasGqlAttachment) => {
      const timestamp = Number(attachment.capturedAt ?? attachment.created);
      return Number.isFinite(timestamp) ? timestamp : null;
    };
    const aTimestamp = getTimestamp(a);
    const bTimestamp = getTimestamp(b);

    if (aTimestamp === null && bTimestamp === null) {
      return 0;
    }
    if (aTimestamp === null) {
      return 1;
    }
    if (bTimestamp === null) {
      return -1;
    }
    return aTimestamp - bTimestamp;
  },
  render: (record: AtlasGqlAttachment) => {
    const timestamp = record?.capturedAt ?? record?.created;
    // Attachment timestamps are millisecond strings, so trim 3 characters for moment.
    return timestamp ? formatUnixTimestamp(Number(timestamp.slice(0, -3))) : <None />;
  },
};

export const actions = ({
  onRemove,
  onModalOpen,
  isRemoveVisible = true,
}: {
  onRemove: (record: AtlasGqlAttachment) => void;
  onModalOpen: (record: AtlasGqlAttachment) => void;
  isRemoveVisible: boolean;
}) => ({
  title: i18n.t('table.actions', 'Actions'),
  width: '25%',
  render: (record: AtlasGqlAttachment) => {
    if (!record) {
      return <None message={i18n.t('common.notApplicable', 'N/A')} />;
    }
    const { canDelete, canUpdate } = record?.permissions;

    return (
      <>
        <a href={`${record.url}`} download={record.name} target="_blank" rel="noopener noreferrer">
          {i18n.t('table.download', 'Download')}
        </a>
        {onRemove && canDelete && isRemoveVisible && (
          <>
            <Divider type="vertical" />
            <Popconfirm
              title={i18n.t('attachments.confirmRemove', 'Sure to remove?')}
              onConfirm={() => onRemove(record)}
            >
              <ActionButton>{i18n.t('common.remove', 'Remove')}</ActionButton>
            </Popconfirm>
          </>
        )}
        {canUpdate && isRemoveVisible && (
          <>
            <Divider type="vertical" />
            <ActionButton data-testid="edit-attachment" onClick={() => onModalOpen(record)}>
              {i18n.t('common.edit', 'Edit')}
            </ActionButton>
          </>
        )}
      </>
    );
  },
});
